import {useRouter} from 'next/router';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {useInView} from 'react-intersection-observer';
import {openCashCharge} from '../../../../cashcharge/openCashCharge.function';
import {DEFAULT_RENTAL_PERIOD_BY_MINUTE} from '../../../../common/constant';
import {t} from '../../../../common/getTranslations';
import {requestContext} from '../../../../common/requestContext';
import {TRIGGER_COMMAND_KEY} from '../../../../common/shared/constants';
import {
  COMMAND_KEY,
  useCommandWithLocalStorage,
} from '../../../../common/shared/hooks/useCommandWithLocalStorage';
import {formatWithCommas} from '../../../../common/shared/utils/formatWithCommas';
import {isTrue} from '../../../../common/shared/utils/isTrue';
import {twMerge} from '../../../../common/twMerge';
import {ButtonV2Variant} from '../../../../design-system/atoms/ButtonV2/ButtonV2.enum';
import {Divider} from '../../../../design-system/atoms/Divider/Divider';
import {Text} from '../../../../design-system/atoms/Text/Text';
import {BuyTicketBannerList} from '../../../../design-system/organisms/BuyTicketBannerList/BuyTicketBannerList';
import {BuyTicketChargeArea} from '../../../../design-system/organisms/BuyTicketChargeArea/BuyTicketChargeArea';
import {BuyTicketPurchasableItem} from '../../../../design-system/organisms/BuyTicketPurchasableItem/BuyTicketPurchasableItem';
import {BuyTicketRegulation} from '../../../../design-system/organisms/BuyTicketRegulation/BuyTicketRegulation';
import {BuyTicketTopInfo} from '../../../../design-system/organisms/BuyTicketTopInfo/BuyTicketTopInfo';
import {HeaderTitle} from '../../../../design-system/organisms/HeaderTitle/HeaderTitle';
import {useDialog} from '../../../../dialog/useDialog.hook';
import {commonErrorHandler} from '../../../../errors/commonErrorHandler';
import {getSerializedPagewebError} from '../../../../errors/getSerializedPagewebError.function';
import {
  ContentMyTicket,
  ContentTicketInfo,
  Maybe,
  SimpleImageBanner,
  TicketInfoApiFragment,
  TicketKind,
} from '../../../../graphql/generated/types';
import {aggregateAndSendLog} from '../../../../logging/aggregateAndSendLog';
import {LOG_CLICK_EVENT} from '../../../../logging/logEvent';
import {putTiaraObjectProps} from '../../../../logging/putTiaraObjectProps';
import {ONLY_DATA_CONFIG} from '../../../../reactQuery/constant';
import {theme} from '../../../../theme';
import {showToast} from '../../../../toast/toastHandler';
import {useContentHomeOverviewQuery} from '../../../../wrapper/contentWrapper';
import {useUserCashQuery} from '../../../../wrapper/storeWrapper';
import {useContentMyTicketQuery} from '../../../../wrapper/ticketWrapper';
import {ContentHomeTabType} from '../../../content/[seriesId]/type';
import {generateRelayRedirectUrl} from '../../../relay/redirect/util/generateRelayRedirectUrl';
import {QUERY_KEY_VIEWER_TICKET_TYPE_TO_USE} from '../constant';
import {useBuyTicketMutationHandler} from '../hook/useBuyTicketMutationHandler';
import {AttemptToBuyTicketInfo, BuyAtOnceInfo} from '../type';
import {calculateBuyAtOnceInfo} from '../util/calculateBuyAtOnceInfo';
import {getAttempToBuyTicketInfoFromSessionStorage} from '../util/getAttempToBuyTicketInfoFromSessionStorage';
import {pickDirectUseTicketType} from '../util/pickDirectUseTicketType';
import {storeAttemptToBuyTicketInfoToSessionStorage} from '../util/storeAttemptToBuyTicketInfoToSessionStorage';

interface Props {
  seriesId: number;
  productId?: number;
  from?: number;
  waitfreeBlocked?: boolean;
  contentMyTicket: Maybe<ContentMyTicket>;
  ticketInfo?: ContentTicketInfo;
  bannerList?: Array<SimpleImageBanner>;
}

export function BuyTicketContainer({
  seriesId,
  productId,
  from,
  waitfreeBlocked,
  contentMyTicket,
  ticketInfo,
  bannerList,
}: Props) {
  const {isMobile} = requestContext.requestInfo.display;
  const router = useRouter();

  const {handleBuyTicketAsyncMutation} = useBuyTicketMutationHandler();

  /**
   * 이용권 충전 미판매시 화면의 pv는 진입 시점에는 알기 어려운 정보라 페이지뷰로그에 정의하지 않고 이벤트로그 시트에 남기기로 하였습니다.
   * https://docs.google.com/spreadsheets/d/1tFQb3yzpwVp_iQtZr5eHM5QICYN7Rx0fBWbQPkeZb_M/edit?disco=AAAAct9SDH4
   */
  const {ref, inView, entry} = useInView({triggerOnce: true});
  useEffect(() => {
    if (inView && entry?.target) {
      aggregateAndSendLog({
        targetElem: entry.target as HTMLElement,
        actionType: 'Event',
        actionName: '이용권충전_미판매_화면',
        customProps: {
          user_action: 'pv',
        },
      });
    }
  }, [entry, inView]);

  const {
    refetch: refetchUserCash,
    data: userCashData,
    isFetched,
  } = useUserCashQuery(undefined, {refetchOnMount: true});
  const cash = userCashData?.userCash;
  const overviewData = useContentHomeOverviewQuery({seriesId}, ONLY_DATA_CONFIG)
    .data?.contentHomeOverview;
  const [purchasedTicketCount, setPurchasedTicketCount] = useState(0);
  const [isChargingCompleted, setIsChargingCompleted] = useState(false);

  const routeToContentHome = useCallback(() => {
    if (productId) {
      // 이용권 충전 플로우를 통해 뷰어에서 바로 충전으로 진입했던 경우는 replace 합니다.
      router.replace(
        `/content/${seriesId}${
          isMobile ? `?tab_type=${ContentHomeTabType.product}` : ''
        }`
      );
    } else {
      router.push(
        `/content/${seriesId}${
          isMobile ? `?tab_type=${ContentHomeTabType.product}` : ''
        }`
      );
    }
  }, [isMobile, productId, router, seriesId]);
  const onCloseDialog = useCallback(() => {
    // 충전 완료 팝업인 경우에는 딤 영역을 클릭해서 닫을 때에도 확인 버튼과 동일하게 동작합니다.
    if (isChargingCompleted) {
      routeToContentHome();
    }
  }, [isChargingCompleted, routeToContentHome]);
  const {DialogWrapper, Dialog, showDialog, hideDialog} = useDialog({
    isMobile,
    onClose: onCloseDialog,
  });

  const [attemptToBuyTicketInfo, setAttemptToBuyTicketInfo] =
    useState<AttemptToBuyTicketInfo | null>(null);
  const attemptToBuyTicket = useCallback(
    (ticketInfo: TicketInfoApiFragment | BuyAtOnceInfo) => {
      if (
        'totalPrice' in ticketInfo &&
        ticketInfo.totalPrice > MAX_CHARGING_PRICE
      ) {
        showToast({
          message: t('purchase_ticket_charge_up_to', {
            values: {
              value: formatWithCommas(MAX_CHARGING_PRICE),
            },
          }),
          isMobile,
        });
        return;
      }
      setAttemptToBuyTicketInfo({seriesId, ...ticketInfo, quantity: 1});
      showDialog();
    },
    [isMobile, seriesId, showDialog]
  );

  const onClickMinusQuantity = useCallback(() => {
    if (!attemptToBuyTicketInfo || !('quantity' in attemptToBuyTicketInfo)) {
      return;
    }
    setAttemptToBuyTicketInfo({
      ...attemptToBuyTicketInfo,
      quantity:
        attemptToBuyTicketInfo.quantity > 1
          ? attemptToBuyTicketInfo.quantity - 1
          : attemptToBuyTicketInfo.quantity,
    });
  }, [attemptToBuyTicketInfo]);
  const onClickPlusQuantity = useCallback(() => {
    if (!attemptToBuyTicketInfo || !('quantity' in attemptToBuyTicketInfo)) {
      return;
    }
    const desiredQuantity = attemptToBuyTicketInfo.quantity + 1;
    const desiredTotalPrice = attemptToBuyTicketInfo.price * desiredQuantity;
    if (desiredTotalPrice > MAX_CHARGING_PRICE) {
      showToast({
        message: t('purchase_ticket_charge_up_to', {
          values: {
            value: formatWithCommas(MAX_CHARGING_PRICE),
          },
        }),
        isMobile,
      });
      return;
    }
    setAttemptToBuyTicketInfo({
      ...attemptToBuyTicketInfo,
      quantity: desiredQuantity,
    });
  }, [attemptToBuyTicketInfo, isMobile]);

  const {saveCommand} = useCommandWithLocalStorage();

  const routeToViewer = useCallback(() => {
    if (attemptToBuyTicketInfo) {
      if (from) {
        saveCommand({
          commandKey: COMMAND_KEY.TRIGGER_FROM_PREV_PRODUCT,
          command: {
            from,
            location: `/content/${seriesId}/viewer/${productId}`,
          },
        });
      }
      router.replace(
        `/content/${seriesId}/viewer/${productId}?${QUERY_KEY_VIEWER_TICKET_TYPE_TO_USE}=${pickDirectUseTicketType(
          attemptToBuyTicketInfo
        )}&${TRIGGER_COMMAND_KEY}=${COMMAND_KEY.TRIGGER_FROM_PREV_PRODUCT}`
      );
    }
  }, [attemptToBuyTicketInfo, from, productId, router, saveCommand, seriesId]);

  const rentAtOnceInfo = useMemo(() => {
    if (
      !contentMyTicket?.notReadCount ||
      contentMyTicket.notReadCount === 0 ||
      contentMyTicket.notReadCount - contentMyTicket.ticketRentalCount <= 0
    ) {
      return;
    }
    return calculateBuyAtOnceInfo({
      ticketKind: TicketKind.Rent,
      purchasableTicketList: ticketInfo?.purchasableRentalTicketList,
      ticketlessCount: contentMyTicket?.notReadCount,
      ticketCount: contentMyTicket?.ticketRentalCount,
    });
  }, [contentMyTicket, ticketInfo?.purchasableRentalTicketList]);

  const ownAtOnceInfo = useMemo(() => {
    if (
      !contentMyTicket?.notOwnCount ||
      contentMyTicket.notOwnCount === 0 ||
      contentMyTicket.notOwnCount - contentMyTicket.ticketOwnCount <= 0
    ) {
      return;
    }
    return calculateBuyAtOnceInfo({
      ticketKind: TicketKind.Own,
      purchasableTicketList: ticketInfo?.purchasableOwnTicketList,
      ticketlessCount: contentMyTicket?.notOwnCount,
      ticketCount: contentMyTicket?.ticketOwnCount,
    });
  }, [contentMyTicket, ticketInfo?.purchasableOwnTicketList]);

  const {refetch: refetchMyTicket} = useContentMyTicketQuery({
    seriesId,
    includeWaitfree: true,
  });
  const handleBuyTicket = useCallback(
    async (bypassToViewerAfterBuyTicket = false) => {
      if (!attemptToBuyTicketInfo) {
        return;
      }
      const totalAttemptPrice =
        'totalPrice' in attemptToBuyTicketInfo
          ? attemptToBuyTicketInfo.totalPrice
          : attemptToBuyTicketInfo.price * attemptToBuyTicketInfo.quantity;
      if (!cash?.remainCash || cash?.remainCash < totalAttemptPrice) {
        // 캐시충전이 완료된 이후 즉시 티켓을 구매할 수 있도록 세션 스토리지에 현재 상태를 저장합니다.
        storeAttemptToBuyTicketInfoToSessionStorage({
          attemptToBuyTicketInfo,
        });
        openCashCharge({
          returnUrl: generateRelayRedirectUrl(
            `${window.location.href}&trigger_buy_ticket=true${
              from ? `&from=${from}` : ''
            }`
          ),
          chargeAmount: totalAttemptPrice - (cash?.remainCash || 0),
          cashChargeInfo: {
            event_series_id: String(seriesId),
            cash_cvr: '콘텐츠홈',
          },
        });
        return;
      }
      try {
        const {buyTicket} = await handleBuyTicketAsyncMutation(
          attemptToBuyTicketInfo
        );

        if (buyTicket?.remainCash === undefined) {
          return;
        }

        setPurchasedTicketCount(buyTicket.purchasedTicketCount);
        // 이용권 구매가 완료되면 내 이용권을 갱신해줍니다.
        await refetchMyTicket();
        await refetchUserCash();
        aggregateAndSendLog({
          actionType: 'Event',
          actionName: '구매_완료',
          actionKind: 'Purchase',
          ecommerce: {
            price_amt: String(totalAttemptPrice),
            order_no: '0',
          },
          customProps: {
            user_action: 'purchase',
            popup_type: '이용권구매확인팝업',
            event_series_id: String(seriesId),
            event_ticket_type:
              attemptToBuyTicketInfo.ticketKind === TicketKind.Rent
                ? '대여'
                : '소장',
            event_ticket_cnt: String(
              !('totalPrice' in attemptToBuyTicketInfo) &&
                attemptToBuyTicketInfo.quantity
            ),
            is_charged: bypassToViewerAfterBuyTicket ? 'y' : 'n',
          },
        });
        if (bypassToViewerAfterBuyTicket) {
          routeToViewer();
        } else {
          setIsChargingCompleted(true);
          showDialog();
        }
      } catch (e) {
        commonErrorHandler(getSerializedPagewebError(e));
      }
    },
    [
      attemptToBuyTicketInfo,
      cash?.remainCash,
      from,
      handleBuyTicketAsyncMutation,
      refetchMyTicket,
      refetchUserCash,
      routeToViewer,
      seriesId,
      showDialog,
    ]
  );

  useEffect(() => {
    if (!isFetched) {
      return;
    }
    const prevAttemptToBuyTicketInfo =
      getAttempToBuyTicketInfoFromSessionStorage();
    if (!prevAttemptToBuyTicketInfo) {
      return;
    }

    if (seriesId === prevAttemptToBuyTicketInfo.seriesId) {
      setAttemptToBuyTicketInfo(prevAttemptToBuyTicketInfo);
    }
  }, [productId, router, seriesId, isFetched]);

  useEffect(() => {
    if (
      router.isReady &&
      isTrue(router.query['trigger_buy_ticket'] as string) &&
      attemptToBuyTicketInfo &&
      contentMyTicket // ticket/buy 트랜잭션 도중 ticket/my 가 발생하면 사용자는 오류 화면을 만나게 됩니다. 반드시 ticket/my 호출된 뒤에 ticket/buy 가 호출돼야 합니다
    ) {
      router.replace(
        `/buy/ticket?seriesId=${seriesId}${
          productId ? `&productId=${productId}` : ''
        }`,
        undefined,
        {
          shallow: true,
          scroll: false,
        }
      );
      handleBuyTicket(!!productId);
    }
  }, [
    attemptToBuyTicketInfo,
    handleBuyTicket,
    productId,
    router,
    seriesId,
    contentMyTicket,
  ]);

  return (
    <>
      {!isMobile && (
        <HeaderTitle className="mb-32pxr mt-20pxr px-15pxr">
          {t('ticket_purchase_title')}
        </HeaderTitle>
      )}
      <div className="flex flex-col flex-1">
        <BuyTicketTopInfo
          className="bg-theme-solid-10 p-15pxr"
          cash={cash?.remainCash || 0}
          seriesTitle={overviewData?.content?.title || '-'}
        />
        {waitfreeBlocked && (
          <div className="flex flex-col px-15px">
            <Text className="flex items-center justify-center font-small2 h-36px text-theme-solid-100">
              {t('purchase_ticket_popup_not_wuf_info')}
            </Text>
            <Divider
              className="w-full"
              sizePx={1}
              color={theme.colors['theme-transparent-10']}
            />
          </div>
        )}
        {ticketInfo?.isSaleRentalTicket && (
          <BuyTicketChargeArea
            ticketKind={TicketKind.Rent}
            notReadCount={contentMyTicket?.notReadCount}
            possessionTicketCount={contentMyTicket?.ticketRentalCount}
            shouldShowLastDivider={true}
            {...putTiaraObjectProps({
              click: {layer1: '이용권상품'},
            })}
          >
            {ticketInfo?.purchasableRentalTicketList.map(
              (purchasableItem, index) => (
                <BuyTicketPurchasableItem
                  key={purchasableItem.ticketId}
                  ticketKind={TicketKind.Rent}
                  ticketTitle={`${formatWithCommas(
                    purchasableItem.totalTicketCount
                  )}장`}
                  discount={purchasableItem.discount}
                  price={purchasableItem.price}
                  onClickPurchase={e => {
                    LOG_CLICK_EVENT('대여권_클릭', e, () =>
                      attemptToBuyTicket(purchasableItem)
                    );
                  }}
                  {...putTiaraObjectProps({
                    click: {
                      ordnum: String(index + 1),
                      copy: `${purchasableItem.totalTicketCount}장`,
                    },
                  })}
                />
              )
            )}
            {rentAtOnceInfo && (
              <BuyTicketPurchasableItem
                ticketKind={TicketKind.Rent}
                ticketTitle={t('ticket_purchase_purchase_all')}
                price={rentAtOnceInfo.totalPrice}
                discount={rentAtOnceInfo.totalDiscount}
                totalQuantity={rentAtOnceInfo.remainNotReadCount}
                onClickPurchase={e => {
                  LOG_CLICK_EVENT('대여권_클릭', e, () =>
                    attemptToBuyTicket(rentAtOnceInfo)
                  );
                }}
                {...putTiaraObjectProps({
                  click: {
                    ordnum: String(
                      ticketInfo?.purchasableRentalTicketList.length + 1
                    ),
                    copy: '한번에구매하기',
                  },
                })}
              />
            )}
          </BuyTicketChargeArea>
        )}
        {ticketInfo?.isSaleOwnTicket && (
          <BuyTicketChargeArea
            ticketKind={TicketKind.Own}
            notReadCount={contentMyTicket?.notOwnCount}
            possessionTicketCount={contentMyTicket?.ticketOwnCount}
            className="mt-3pxr"
            {...putTiaraObjectProps({
              click: {layer1: '이용권상품'},
            })}
          >
            {ticketInfo?.purchasableOwnTicketList.map(
              (purchasableItem, index) => (
                <BuyTicketPurchasableItem
                  key={purchasableItem.ticketId}
                  ticketKind={TicketKind.Own}
                  ticketTitle={`${purchasableItem.totalTicketCount}장`}
                  price={purchasableItem.price}
                  discount={purchasableItem.discount}
                  onClickPurchase={e => {
                    LOG_CLICK_EVENT('소장권_클릭', e, () =>
                      attemptToBuyTicket(purchasableItem)
                    );
                  }}
                  {...putTiaraObjectProps({
                    click: {
                      ordnum: String(index + 1),
                      copy: `${purchasableItem.totalTicketCount}장`,
                    },
                  })}
                />
              )
            )}
            {ownAtOnceInfo && (
              <BuyTicketPurchasableItem
                ticketKind={TicketKind.Own}
                ticketTitle={t('ticket_purchase_purchase_all')}
                price={ownAtOnceInfo.totalPrice}
                discount={ownAtOnceInfo.totalDiscount}
                totalQuantity={ownAtOnceInfo.remainNotReadCount}
                onClickPurchase={e => {
                  LOG_CLICK_EVENT('소장권_클릭', e, () =>
                    attemptToBuyTicket(ownAtOnceInfo)
                  );
                }}
                {...putTiaraObjectProps({
                  click: {
                    ordnum: String(
                      ticketInfo?.purchasableOwnTicketList.length + 1
                    ),
                    copy: '한번에구매하기',
                  },
                })}
              />
            )}
          </BuyTicketChargeArea>
        )}
        {ticketInfo?.purchasableRentalTicketList.length !== 0 ||
        ticketInfo?.purchasableOwnTicketList.length !== 0 ? (
          <>
            <BuyTicketBannerList bannerList={bannerList} className="mt-3pxr" />
            {ticketInfo && (
              <BuyTicketRegulation
                rentalPeriodByMinute={
                  ticketInfo?.rentalPeriodByMinute ??
                  DEFAULT_RENTAL_PERIOD_BY_MINUTE
                }
                className="bg-theme-bg-base px-15pxr pt-24pxr pb-28pxr"
              />
            )}
          </>
        ) : (
          <div ref={ref} className="flex flex-col flex-1">
            <div className="min-h-500pxr bg-theme-bg-base flex-center-full">
              <Text className="h-full text-theme-solid-100">
                {t('ticket_purchase_error_not_on_sale_ticket')}
              </Text>
            </div>
          </div>
        )}

        {!isChargingCompleted && attemptToBuyTicketInfo && (
          <DialogWrapper
            {...putTiaraObjectProps({
              click: {
                layer1:
                  'remainNotReadCount' in attemptToBuyTicketInfo
                    ? '이용권한번에구매팝업'
                    : '이용권구매팝업',
                imp_id:
                  'remainNotReadCount' in attemptToBuyTicketInfo
                    ? 'popup_tt'
                    : 'popup_t',
                imp_provider: 'service',
              },
              customProps: {
                popup_type: '이용권구매팝업',
                event_series_id: String(seriesId),
                cash_amt: String(
                  formatWithCommas(
                    'remainNotReadCount' in attemptToBuyTicketInfo
                      ? attemptToBuyTicketInfo.totalPrice
                      : attemptToBuyTicketInfo.quantity *
                          attemptToBuyTicketInfo.price
                  )
                ),
                page_ticket_type:
                  attemptToBuyTicketInfo.ticketKind === TicketKind.Rent
                    ? '대여'
                    : '소장',
                page_ticket_cnt: !(
                  'remainNotReadCount' in attemptToBuyTicketInfo
                )
                  ? String(
                      attemptToBuyTicketInfo.quantity *
                        attemptToBuyTicketInfo.totalTicketCount
                    )
                  : String(attemptToBuyTicketInfo.remainNotReadCount),
              },
              viewimp_contents: {
                imp_id:
                  'remainNotReadCount' in attemptToBuyTicketInfo
                    ? 'popup_tt'
                    : 'popup_t',
                imp_provider: 'service',
                imp_type: 'vimp',
                layer1:
                  'remainNotReadCount' in attemptToBuyTicketInfo
                    ? '이용권한번에구매팝업'
                    : '이용권구매팝업',
              },
            })}
            onInView={target => {
              aggregateAndSendLog({
                targetElem: target as HTMLElement,
                actionType: 'ViewImp',
                actionName: '팝업_노출',
                customProps: {
                  user_action: 'imp',
                },
              });
            }}
          >
            <Dialog.Dimmed />
            <Dialog.List className="pb-16pxr">
              <Dialog.TicketCountControl
                title={overviewData?.content?.title || '-'}
                ticketKind={attemptToBuyTicketInfo.ticketKind}
                isBuyAtOnce={'remainNotReadCount' in attemptToBuyTicketInfo}
                {...(!('remainNotReadCount' in attemptToBuyTicketInfo) && {
                  totalTicketCount: attemptToBuyTicketInfo.totalTicketCount,
                  quantity: attemptToBuyTicketInfo.quantity,
                })}
                onClickMinus={e => {
                  LOG_CLICK_EVENT('수량조절버튼_클릭', e, onClickMinusQuantity);
                }}
                onClickPlus={e => {
                  LOG_CLICK_EVENT('수량조절버튼_클릭', e, onClickPlusQuantity);
                }}
              />
            </Dialog.List>
            <Dialog.List className="space-y-16pxr">
              <Dialog.Divider />
              <div className="flex justify-between">
                <Dialog.Label>
                  {t('ticket_purchase_confirmation_popup_total_count')}
                </Dialog.Label>
                <Dialog.Label className="font-medium1-bold">
                  {t('common_ticket_amount', {
                    values: {
                      value: formatWithCommas(
                        'remainNotReadCount' in attemptToBuyTicketInfo
                          ? attemptToBuyTicketInfo.remainNotReadCount
                          : attemptToBuyTicketInfo.quantity *
                              attemptToBuyTicketInfo.totalTicketCount
                      ),
                    },
                  })}
                </Dialog.Label>
              </div>
              <div className="flex justify-between">
                <Dialog.Label>
                  {t('ticket_purchase_confirmation_popup_total_payment')}
                </Dialog.Label>
                <Dialog.Label className="font-medium1-bold">
                  {t('common_cash_amount_sp', {
                    values: {
                      value: formatWithCommas(
                        'remainNotReadCount' in attemptToBuyTicketInfo
                          ? attemptToBuyTicketInfo.totalPrice
                          : attemptToBuyTicketInfo.quantity *
                              attemptToBuyTicketInfo.price
                      ),
                    },
                  })}
                </Dialog.Label>
              </div>
              <Dialog.Divider />
            </Dialog.List>
            <Dialog.List className="space-y-0">
              <Dialog.Caption>
                {t('ticket_purchase_confirmation_popup_info_1')}
              </Dialog.Caption>
              <Dialog.Caption>
                {t('ticket_purchase_confirmation_popup_info_2')}
              </Dialog.Caption>
            </Dialog.List>
            <Dialog.BottomCombination>
              <Dialog.Button
                onClick={e => {
                  LOG_CLICK_EVENT('취소_클릭', e, hideDialog);
                }}
              >
                {t('common_cancel_button')}
              </Dialog.Button>
              <Dialog.Button
                onClick={e => {
                  LOG_CLICK_EVENT('충전하기_클릭', e, () => handleBuyTicket());
                }}
                variant={ButtonV2Variant.Primary}
              >
                {t('ticket_purchase_confirmation_popup_purchase')}
              </Dialog.Button>
            </Dialog.BottomCombination>
          </DialogWrapper>
        )}
        {isChargingCompleted && attemptToBuyTicketInfo && (
          <DialogWrapper
            {...putTiaraObjectProps({
              click: {
                layer1: '이용권구매확인팝업',
              },
              customProps: {
                popup_type: '이용권구매확인팝업',
              },
              viewimp_contents: {
                imp_id: 'popup',
                imp_provider: 'service',
                imp_type: 'vimp',
                layer1: '이용권구매확인팝업',
              },
            })}
            onInView={target => {
              aggregateAndSendLog({
                targetElem: target as HTMLElement,
                actionType: 'ViewImp',
                actionName: '팝업_노출',
                customProps: {
                  user_action: 'imp',
                },
              });
            }}
          >
            <Dialog.Dimmed />
            <Dialog.Title>
              {t(
                attemptToBuyTicketInfo.ticketKind === TicketKind.Rent
                  ? 'ticket_purchase_rental_successful_popup'
                  : 'ticket_purchase_possession_successful_popup',
                {
                  values: {
                    value: formatWithCommas(purchasedTicketCount),
                  },
                }
              )}
            </Dialog.Title>
            <Dialog.Label>
              {t('common_cash_balance')}:{' '}
              {t('common_cash_amount', {
                values: {
                  value:
                    cash?.remainCash || cash?.remainCash === 0
                      ? formatWithCommas(cash.remainCash)
                      : '-',
                },
              })}
            </Dialog.Label>
            <Dialog.BottomCombination
              className={twMerge(isMobile && 'mt-20pxr')}
            >
              <Dialog.Button
                onClick={e => {
                  LOG_CLICK_EVENT(
                    '구매완료팝업_확인_클릭',
                    e,
                    routeToContentHome
                  );
                }}
              >
                {t('common_confirm_button')}
              </Dialog.Button>
              {productId && (
                <Dialog.Button
                  onClick={routeToViewer}
                  variant={ButtonV2Variant.Primary}
                >
                  {t('purchase_successful_popup_view_now_button')}
                </Dialog.Button>
              )}
            </Dialog.BottomCombination>
          </DialogWrapper>
        )}
      </div>
    </>
  );
}

const MAX_CHARGING_PRICE = 500_000;
